import React, { useState, useEffect } from 'react';
import { FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import { useUser } from '../../contexts/UserContext';
import { chatApi } from '../../services/api';
import FeedbackModal from './FeedbackModal';

interface FeedbackButtonsProps {
  messageId: string;
  jobId?: string;
  conversationId?: string;
  hasVisualization: boolean;
  hasSearchEnrichment: boolean;
  initialRating?: boolean;
  onFeedbackSubmitted?: (rating: boolean) => void;
}

const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  messageId,
  jobId,
  conversationId,
  hasVisualization,
  hasSearchEnrichment,
  initialRating,
  onFeedbackSubmitted,
}) => {
  const { userEmail } = useUser();
  const [submittedRating, setSubmittedRating] = useState<boolean | undefined>(initialRating);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingRating, setPendingRating] = useState<boolean>(true);

  // Reconcile with backend if we have a jobId but no local state (e.g. imported conversations)
  useEffect(() => {
    if (jobId && submittedRating === undefined && userEmail) {
      chatApi.checkFeedback(jobId, userEmail).then(result => {
        if (result.has_feedback && result.overall_rating !== undefined) {
          setSubmittedRating(result.overall_rating);
        }
      }).catch(() => {});
    }
  }, [jobId, userEmail, submittedRating]);

  if (!jobId) return null;

  const handleThumbClick = (rating: boolean) => {
    setPendingRating(rating);
    setIsModalOpen(true);
  };

  const handleFeedbackSubmitted = (rating: boolean) => {
    setSubmittedRating(rating);
    setIsModalOpen(false);
    onFeedbackSubmitted?.(rating);
  };

  const isSubmitted = submittedRating !== undefined;

  return (
    <>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-xs text-text-tertiary dark:text-dark-textSecondary mr-1">
          {isSubmitted ? 'Feedback submitted' : 'Was this helpful?'}
        </span>
        <button
          onClick={() => handleThumbClick(true)}
          disabled={isSubmitted}
          className={`p-1.5 rounded-lg transition-all duration-200 ${
            submittedRating === true
              ? 'text-green-500 bg-green-50 dark:bg-green-900/20'
              : isSubmitted
                ? 'text-text-tertiary/40 dark:text-dark-textSecondary/40 cursor-default'
                : 'text-text-tertiary dark:text-dark-textSecondary hover:text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'
          }`}
          aria-label="Thumbs up"
        >
          <FiThumbsUp size={15} />
        </button>
        <button
          onClick={() => handleThumbClick(false)}
          disabled={isSubmitted}
          className={`p-1.5 rounded-lg transition-all duration-200 ${
            submittedRating === false
              ? 'text-red-500 bg-red-50 dark:bg-red-900/20'
              : isSubmitted
                ? 'text-text-tertiary/40 dark:text-dark-textSecondary/40 cursor-default'
                : 'text-text-tertiary dark:text-dark-textSecondary hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
          }`}
          aria-label="Thumbs down"
        >
          <FiThumbsDown size={15} />
        </button>
      </div>

      <FeedbackModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialRating={pendingRating}
        jobId={jobId}
        conversationId={conversationId}
        hasVisualization={hasVisualization}
        hasSearchEnrichment={hasSearchEnrichment}
        onSubmitted={handleFeedbackSubmitted}
      />
    </>
  );
};

export default FeedbackButtons;
